import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, IAppReduxStore, SlicePropsWithData } from '../../store';
import { IDocDefaultFields } from './sliceProjectDocuments';
import {
    urlParamReplicaId,
    urlParamUniversalId,
    urlProjectDocumentData,
    urlProjectDocumentDataPermissions,
    urlProjectDocumentDataSave,
    urlProjectDocumentMetadata,
} from '../../../Constants';
import { IPermission } from '@eplass/formdesigner/permissions/interfaces';
import { fileSave } from 'browser-fs-access';
import { values } from 'lodash';
import { FormdesignersModeTypes } from '@eplass/formdesigner/domain/mode.types';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentDetailsState = {
    isDialogOpen: false,
    isDialogExpanded: false,
    isLeaveConfirmDialogOpen: false,
    isSaveButtonDisabled: true,
    isSaveButtonLoaded: false,
    isDocumentCreated: false,
    data: {} as IDocumentDetailsEntry,
    loading: false,
    error: null,
    docTypeKeyForTemplateCreation: null,
    formdesignerData: {
        data: null,
        loading: true,
        error: null,
    },
    formdesignerPermissions: {
        data: {} as IPermission,
        loading: true,
        error: null,
    },
    documentDetailsMode: FormdesignersModeTypes.View,
    loadingSaveSchema: false,
    errorSaveSchema: null,
    loadingSaveForm: false,
    errorSaveForm: null,
};

/** Function for demo goals */
const writeFile = async (data: any, prop?: string) => {
    const str = JSON.stringify(prop ? data?.[prop] : data);
    const bytes = new TextEncoder().encode(str);
    const blob = new Blob([bytes]);
    await fileSave(blob, {
        fileName: 'demo.json',
    });
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    closeDocumentDetailsDialog(state: IProjectDocumentDetailsState, action: IAction<boolean>) {
        if (!state.isSaveButtonDisabled && action.payload) {
            state.isLeaveConfirmDialogOpen = true;
        } else {
            state.isDialogOpen = false;
            state.documentDetailsMode = FormdesignersModeTypes.View;
            state.docTypeKeyForTemplateCreation = null;
            state.data = {} as IDocumentDetailsEntry;
            state.formdesignerData.data = null;
            state.formdesignerPermissions.data = {} as IPermission;
            state.isDocumentCreated = false;
        }
    },
    openDocumentDetailsDialog(state: IProjectDocumentDetailsState, action?: IAction<string>) {
        state.isDialogOpen = true;
        if (action.payload) {
            state.docTypeKeyForTemplateCreation = action.payload;
            state.documentDetailsMode = FormdesignersModeTypes.Edit;
        }
    },
    closeLeaveConfirmDialog(state: IProjectDocumentDetailsState) {
        state.isLeaveConfirmDialogOpen = false;
    },
    toggleExpandDocumentDetailsDialog(state: IProjectDocumentDetailsState) {
        state.isDialogExpanded = !state.isDialogExpanded;
    },
    toggleSaveButton(state: IProjectDocumentDetailsState, action: IAction<boolean>) {
        state.isSaveButtonDisabled = action.payload;
    },
    toggleIsDocumentCreated(state: IProjectDocumentDetailsState, action: IAction<boolean>) {
        state.isDocumentCreated = action.payload;
    },
    setSaveButtonLoading(state: IProjectDocumentDetailsState, action: IAction<boolean>) {
        state.isSaveButtonLoaded = action.payload;
    },
    handleEditMode(state: IProjectDocumentDetailsState, action?: IAction<FormdesignersModeTypes>) {
        if (action.payload) {
            state.documentDetailsMode = action.payload;
            return;
        }
        state.documentDetailsMode =
            state.documentDetailsMode === FormdesignersModeTypes.View
                ? FormdesignersModeTypes.Edit
                : FormdesignersModeTypes.View;
    },
    resetErrorSaveForm(state: IProjectDocumentDetailsState) {
        state.errorSaveForm = null;
    },
};

/*
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches document's details for the particular document in a project.
     * @param config - Object that contains the project database's replicaId, target document number, and the document fields to return.
     */
    getProjectDocumentDetails: async (
        config: IProjectDocumentDetailsConfig,
    ): Promise<IDocumentDetailsEntry> => {
        try {
            const { replicaId, universalId } = config;

            const request: RequestInit = {
                headers: {
                    fieldset: [
                        'docIndex',
                        'docNumber',
                        'creationDate',
                        'creator',
                        'subject',
                        'docStatus',
                        'wfTask',
                        'wfType',
                        'wfTitle',
                    ].join(';'),
                    viewName: 'test',
                },
            };

            const url = urlProjectDocumentMetadata
                .replace(urlParamReplicaId, replicaId)
                .replace(urlParamUniversalId, universalId);

            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Async function that fetches formData for the FormDesigner.
     * @param config Object that containes replicaId, universalId, schemaData.
     * @returns Object of values for FormData in accordance with dataKeys in schemaData.
     */
    getFormData: async (config: IFormDataConfig): Promise<object> => {
        try {
            const { replicaId, universalId, schemaData } = config;

            /** Note: Backend only accepts data in form-format! */
            const formData = new FormData();
            formData.append('schemaJson', schemaData);

            const url = urlProjectDocumentData
                .replace(urlParamReplicaId, replicaId)
                .replace(urlParamUniversalId, universalId);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Async function that fetches permissions for the FormDesigner.
     * @returns Object of permissions
     */
    getPermissions: async (config: IPermissionsConfig): Promise<IPermission> => {
        try {
            const { replicaId, schemaData, docTypeKey, universalId } = config;

            /** Note: Backend only accepts data in form-format! */
            const formData = new FormData();
            formData.append('schemaJson', schemaData);
            formData.append('doctype', docTypeKey);
            universalId && formData.append('universalId', universalId);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const url = urlProjectDocumentDataPermissions.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Saves schema.
     */
    saveSchema: async (updatedSchema: unknown): Promise<void> => {
        console.log('ON_SAVE_SCHEMA', updatedSchema);
        await writeFile(updatedSchema, 'uiSchema');
    },

    /**
     * Saves form.
     */
    saveForm: async (config: ISaveConfig): Promise<void> => {
        try {
            const { replicaId, universalId, updatedForm } = config;

            /** Note: Backend only accepts data in form-format! */
            const formData = new FormData();
            formData.append('documentData', updatedForm);

            const url = urlProjectDocumentDataSave
                .replace(urlParamReplicaId, replicaId)
                .replace(urlParamUniversalId, universalId);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve()
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentDetailsState>) => {
        /**
         * Reducers for the 'getProjectDocumentDetails' thunk.
         */

        builder.addCase(
            getProjectDocumentDetails.pending,
            (state: Draft<IProjectDocumentDetailsState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            getProjectDocumentDetails.fulfilled,
            (
                state: Draft<IProjectDocumentDetailsState>,
                action: IAction<IDocumentDetailsEntry>,
            ) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getProjectDocumentDetails.rejected,
            (state: Draft<IProjectDocumentDetailsState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = {} as IDocumentDetailsEntry;
            },
        );

        /**
         * Reducers for the 'getFormData' thunk.
         */

        builder.addCase(getFormData.pending, (state: Draft<IProjectDocumentDetailsState>) => {
            state.formdesignerData.loading = true;
            state.formdesignerData.error = null;
        });
        builder.addCase(
            getFormData.fulfilled,
            (state: Draft<IProjectDocumentDetailsState>, action: IAction<object>) => {
                state.formdesignerData.loading = false;
                state.formdesignerData.error = null;
                state.formdesignerData.data = action.payload;
            },
        );
        builder.addCase(
            getFormData.rejected,
            (state: Draft<IProjectDocumentDetailsState>, action: IAction<SerializedError>) => {
                state.formdesignerData.loading = false;
                state.formdesignerData.error = action.error;
                state.formdesignerData.data = {} as object;
            },
        );

        /**
         * Reducers for the 'getPermissions' thunk.
         */

        builder.addCase(getPermissions.pending, (state: Draft<IProjectDocumentDetailsState>) => {
            state.formdesignerPermissions.loading = true;
            state.formdesignerPermissions.error = null;
        });
        builder.addCase(
            getPermissions.fulfilled,
            (state: Draft<IProjectDocumentDetailsState>, action: IAction<IPermission>) => {
                state.formdesignerPermissions.loading = false;
                state.formdesignerPermissions.error = null;
                state.formdesignerPermissions.data = action.payload;
            },
        );
        builder.addCase(
            getPermissions.rejected,
            (state: Draft<IProjectDocumentDetailsState>, action: IAction<SerializedError>) => {
                state.formdesignerPermissions.loading = false;
                state.formdesignerPermissions.error = action.error;
                state.formdesignerPermissions.data = {} as IPermission;
            },
        );

        /**
         * Reducers for the 'saveSchema' thunk.
         */

        builder.addCase(saveSchema.pending, (state: Draft<IProjectDocumentDetailsState>) => {
            state.loadingSaveSchema = true;
            state.errorSaveSchema = null;
        });
        builder.addCase(saveSchema.fulfilled, (state: Draft<IProjectDocumentDetailsState>) => {
            state.loadingSaveSchema = false;
            state.errorSaveSchema = null;
        });
        builder.addCase(
            saveSchema.rejected,
            (state: Draft<IProjectDocumentDetailsState>, action: IAction<SerializedError>) => {
                state.loadingSaveSchema = false;
                state.errorSaveSchema = action.error;
            },
        );

        /**
         * Reducers for the 'saveForm' thunk.
         */

        builder.addCase(saveForm.pending, (state: Draft<IProjectDocumentDetailsState>) => {
            state.loadingSaveForm = true;
            state.errorSaveForm = null;
        });
        builder.addCase(saveForm.fulfilled, (state: Draft<IProjectDocumentDetailsState>) => {
            state.loadingSaveForm = false;
            state.errorSaveForm = null;
        });
        builder.addCase(
            saveForm.rejected,
            (state: Draft<IProjectDocumentDetailsState>, action: IAction<SerializedError>) => {
                state.loadingSaveForm = false;
                state.errorSaveForm = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectDocumentDetails = createAsyncThunk(
    'project/documentDetails',
    thunks.getProjectDocumentDetails,
);
export const getFormData = createAsyncThunk('project/formData', thunks.getFormData);
export const getPermissions = createAsyncThunk('project/permissions', thunks.getPermissions);
export const saveSchema = createAsyncThunk('project/schema', thunks.saveSchema);
export const saveForm = createAsyncThunk('project/form', thunks.saveForm);

/**
 * Interface that describes the document's details config.
 */
export interface IProjectDocumentDetailsConfig {
    /** Project replicaId. */
    replicaId: string;

    /** Document's idDocIndex. */
    universalId: string;
}

/**
 * Interface that describes the FormData config.
 */
export interface IFormDataConfig extends IProjectDocumentDetailsConfig {
    /** Schema. */
    schemaData: string;
}

/**
 * Interface that describes the config for saving data.
 */
export interface ISaveConfig extends IProjectDocumentDetailsConfig {
    /** Schema. */
    updatedForm: string;
}

/**
 * Interface that describes the FormData config.
 */
export interface IPermissionsConfig extends Omit<IFormDataConfig, 'universalId'> {
    /** docTypeKey. */
    docTypeKey: string;

    /** Document's idDocIndex. */
    universalId?: string;
}

/**
 * Type that describes the FormDesigner's data.
 */
export type FormDesignerData = SlicePropsWithData<object>;

/**
 * Type that describes permissions.
 */
export type FormDesignerPermissions = SlicePropsWithData<IPermission>;

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectDocumentDetailsState extends SlicePropsWithData<IDocumentDetailsEntry> {
    /** Defines if the Dialog is opened. */
    isDialogOpen: boolean;

    /** Defines if the Dialog is expanded. */
    isDialogExpanded: boolean;

    /** Defines if the LeaveConfirmDialog is opened. */
    isLeaveConfirmDialogOpen: boolean;

    /** Defines if save button disabled. */
    isSaveButtonDisabled: boolean;

    /** Defines if save button loaded. */
    isSaveButtonLoaded: boolean;

    /** Defines if document was created. */
    isDocumentCreated: boolean;

    /** FormDesigner data. */
    formdesignerData: FormDesignerData;

    /** Permissions for FormDesigner. */
    formdesignerPermissions: FormDesignerPermissions;

    /** Defines mode for document details. */
    documentDetailsMode: FormdesignersModeTypes;

    /** Defines if Schema is saving. */
    loadingSaveSchema: boolean;

    /** Error that is thrown during resource fetching. */
    errorSaveSchema: SerializedError;

    /** Defines if Form is saving. */
    loadingSaveForm: boolean;

    /** Error that is thrown during resource fetching. */
    errorSaveForm: SerializedError;

    /** Defines document type. */
    docTypeKeyForTemplateCreation: string | null;
}

/**
 * Interface that describes the Object that is returned from a document's details request.
 */
export interface IDocumentDetailsEntry extends IDocDefaultFields {
    /** Document version index. */
    docIndex: string;

    /** Document number. */
    docNumber: string;

    /** Document creation date. */
    creationDate: number;

    /** User that created the document. */
    creator: string;

    /** Document's subject text. */
    subject: string;

    /** Status of the document. */
    docStatus: string;

    /** Workflow name */
    wfType: string;

    /** Workflow name */
    wfTitle: string;

    /** Workflow task where the document is currently located. */
    wfTask: string;
}

export const projectDocumentDetailsSlice = createSlice({
    name: 'projectDocumentDetails',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const hasSomePermission = (state: IAppReduxStore) => {
    const permissionsFormDataObj =
        state.projectDocumentDetails?.formdesignerPermissions?.data?.formData;
    if (!permissionsFormDataObj) {
        return false;
    }
    const permissionsValues = values(permissionsFormDataObj.fields);
    if (!permissionsValues?.length && permissionsFormDataObj.general?.edit) {
        return true;
    }
    return permissionsValues.some(item => item.edit);
};

/**
 * Export functions from slice actions.
 */
export const {
    closeDocumentDetailsDialog,
    toggleExpandDocumentDetailsDialog,
    openDocumentDetailsDialog,
    handleEditMode,
    toggleSaveButton,
    setSaveButtonLoading,
    closeLeaveConfirmDialog,
    toggleIsDocumentCreated,
    resetErrorSaveForm,
} = projectDocumentDetailsSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectDocumentDetailsSlice.reducer;
